import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";

function getHeaders() {
  const user = JSON.parse(localStorage.getItem("user"));
  return { Authorization: "Bearer " + user?.Token, Accept: "*/*" };
}

const API = process.env.REACT_APP_API_URL;

// ── Thunks ────────────────────────────────────────────────────────

export const getQoBuilder = createAsyncThunk("qoV2/builder", async (_, { rejectWithValue }) => {
  try {
    const res = await axios.get(API + "getQoBuilder", { headers: getHeaders() });
    return res.data;
  } catch (e) {
    return rejectWithValue(e.response?.data);
  }
});

export const getQoDetail = createAsyncThunk("qoV2/detail", async (qoNo, { rejectWithValue }) => {
  try {
    const res = await axios.get(API + "getQoDetail", {
      headers: getHeaders(),
      params: { qoNo },
    });
    return res.data;
  } catch (e) {
    return rejectWithValue(e.response?.data);
  }
});

export const cekQoApprover = createAsyncThunk("qoV2/cekApprover", async (_, { rejectWithValue }) => {
  try {
    const res = await axios.get(API + "isQoApprover", { headers: getHeaders() });
    return res.data;
  } catch (e) {
    return rejectWithValue(e.response?.data);
  }
});

// Cek nomor kontrak kembar. Sengaja TIDAK menyentuh state: hasilnya dipakai
// sekali di form sebagai bahan konfirmasi, bukan sesuatu yang perlu diingat
// halaman lain — makanya tak ada extraReducers untuk thunk ini.
export const cekNoKontrak = createAsyncThunk("qoV2/cekNoKontrak", async ({ NoKontrak, QoNo }, { rejectWithValue }) => {
  try {
    const res = await axios.get(API + "cekNoKontrak", {
      headers: getHeaders(),
      params: { NoKontrak, QoNo: QoNo ?? "" },
    });
    return res.data;
  } catch (e) {
    return rejectWithValue(e.response?.data);
  }
});

export const insertQo = createAsyncThunk("qoV2/insert", async (payload, { rejectWithValue }) => {
  try {
    const res = await axios.post(API + "insertQo", payload, { headers: getHeaders() });
    return res.data;
  } catch (e) {
    return rejectWithValue(e.response?.data);
  }
});

export const updateQo = createAsyncThunk("qoV2/update", async (payload, { rejectWithValue }) => {
  try {
    const res = await axios.post(API + "updateQo", payload, { headers: getHeaders() });
    return res.data;
  } catch (e) {
    return rejectWithValue(e.response?.data);
  }
});

export const getQoList = createAsyncThunk("qoV2/list", async ({ search = "", QoType = "" }, { rejectWithValue }) => {
  try {
    const fd = new FormData();
    fd.append("search", search);
    fd.append("QoType", QoType);
    const res = await axios.post(API + "getQoList", fd, { headers: getHeaders() });
    return res.data;
  } catch (e) {
    return rejectWithValue(e.response?.data);
  }
});

export const updateQoStatus = createAsyncThunk(
  "qoV2/updateStatus",
  async ({ QoNo, Status, Note = "" }, { rejectWithValue }) => {
    try {
      const fd = new FormData();
      fd.append("QoNo", QoNo);
      fd.append("Status", Status);
      fd.append("Note", Note);
      const res = await axios.post(API + "updateQoStatus", fd, { headers: getHeaders() });
      return res.data;
    } catch (e) {
      return rejectWithValue(e.response?.data);
    }
  }
);

// ── Slice ─────────────────────────────────────────────────────────

const initialState = {
  builder: null,
  detail: null,
  list: [],
  // Apakah user yang login pemberi approval Quotation. Dipakai halaman DAFTAR
  // untuk menyembunyikan Edit & Buat JR — di halaman detail informasi yang sama
  // datang dari detail.CanApprove.
  canApproveQo: false,
  canEditApprovedQo: false, // bagian FM-IT/BD boleh edit QO approved
  isBuilderLoading: false,
  isBuilderSuccess: false,
  isDetailLoading: false,
  isDetailSuccess: false,
  isSaving: false,
  isSaveSuccess: false,
  isListLoading: false,
  error: null,
};

const quotationV2Slice = createSlice({
  name: "qoV2",
  initialState,
  reducers: {
    resetSaveStatus(state) {
      state.isSaveSuccess = false;
      state.error = null;
    },
    clearDetail(state) {
      state.detail = null;
      state.isDetailSuccess = false;
    },
  },
  extraReducers: (builder) => {
    // getQoBuilder
    builder
      .addCase(getQoBuilder.pending, (state) => { state.isBuilderLoading = true; })
      .addCase(getQoBuilder.fulfilled, (state, action) => {
        state.isBuilderLoading = false;
        state.isBuilderSuccess = true;
        state.builder = action.payload?.data ?? null;
      })
      .addCase(getQoBuilder.rejected, (state, action) => {
        state.isBuilderLoading = false;
        state.error = action.payload?.message ?? "Gagal load builder";
      });

    // getQoDetail
    builder
      .addCase(getQoDetail.pending, (state) => { state.isDetailLoading = true; })
      .addCase(getQoDetail.fulfilled, (state, action) => {
        state.isDetailLoading = false;
        state.isDetailSuccess = true;
        state.detail = action.payload?.data ?? null;
      })
      .addCase(getQoDetail.rejected, (state, action) => {
        state.isDetailLoading = false;
        state.error = action.payload?.message ?? "Gagal load detail";
      });

    // insertQo
    builder
      .addCase(insertQo.pending, (state) => { state.isSaving = true; state.isSaveSuccess = false; })
      // Gagal cek = anggap BUKAN approver: tombol Edit/Buat JR tetap tampil.
      // Arah aman untuk kesalahan di sini adalah membiarkan pembuat QO bekerja,
      // bukan mengunci semua orang; penjaga sesungguhnya ada di UpdateQoStatus.
      .addCase(cekQoApprover.fulfilled, (state, action) => {
        state.canApproveQo = action.payload?.data?.CanApprove ?? false;
        state.canEditApprovedQo = action.payload?.data?.CanEditApproved ?? false;
      })
      .addCase(cekQoApprover.rejected, (state) => {
        state.canApproveQo = false;
        state.canEditApprovedQo = false;
      })

      .addCase(insertQo.fulfilled, (state, action) => {
        state.isSaving = false;
        state.isSaveSuccess = true;
        state.detail = action.payload?.data ?? null;
      })
      .addCase(insertQo.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload?.message ?? "Gagal menyimpan QO";
      });

    // updateQo
    builder
      .addCase(updateQo.pending, (state) => { state.isSaving = true; state.isSaveSuccess = false; })
      .addCase(updateQo.fulfilled, (state, action) => {
        state.isSaving = false;
        state.isSaveSuccess = true;
        state.detail = action.payload?.data ?? null;
      })
      .addCase(updateQo.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload?.message ?? "Gagal update QO";
      });

    // getQoList
    builder
      .addCase(getQoList.pending, (state) => { state.isListLoading = true; })
      .addCase(getQoList.fulfilled, (state, action) => {
        state.isListLoading = false;
        state.list = action.payload?.data ?? [];
      })
      .addCase(getQoList.rejected, (state, action) => {
        state.isListLoading = false;
        state.error = action.payload?.message ?? "Gagal load list";
      });

    // updateQoStatus
    builder
      .addCase(updateQoStatus.fulfilled, (state, action) => {
        // Will be refetched by the page after this
      });
  },
});

export const { resetSaveStatus, clearDetail } = quotationV2Slice.actions;
export const quotationV2Selector = (state) => state.quotationV2;
export default quotationV2Slice.reducer;
